import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { trackSocialClick } from "@/lib/analytics";
import { 
  Home, 
  Image, 
  User, 
  Palette,
  MessageCircle,
  Phone,
  Mail,
  ShoppingCart,
  ArrowRight,
  ExternalLink
} from "lucide-react";
import OrderDialog from "./OrderDialog";
import { useState } from "react";

interface FloatingActionsProps {
  currentPage?: string;
  showOrderButton?: boolean;
  artworkData?: {
    title: string;
    id: string;
    price: number;
  };
}

const FloatingActions = ({ currentPage = "", showOrderButton = false, artworkData }: FloatingActionsProps) => {
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);

  const navigationLinks = [
    { to: "/", label: "Главная", icon: Home, color: "text-blue-500" },
    { to: "/about", label: "О художнике", icon: User, color: "text-purple-500" },
    { to: "/gallery", label: "Галерея", icon: Image, color: "text-green-500" },
    { to: "/services", label: "Услуги", icon: Palette, color: "text-orange-500" },
  ];

  const contactLinks = [
    { 
      name: "VK", 
      url: "https://vk.com/im?sel=-214021218", 
      color: "text-blue-500 hover:text-blue-600",
      bgColor: "bg-blue-500/10 hover:bg-blue-500/20",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zM12 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6zm6-10.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5z"/>
          <circle cx="12" cy="12" r="3" fill="white"/>
          <text x="12" y="8" textAnchor="middle" fontSize="4" fill="currentColor">VK</text>
        </svg>
      )
    },
    { 
      name: "WhatsApp", 
      url: "https://wa.me/79507818785", 
      color: "text-green-500 hover:text-green-600",
      bgColor: "bg-green-500/10 hover:bg-green-500/20",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
      )
    },
    { 
      name: "Telegram", 
      url: "https://t.me/FallenSonS",
      color: "text-blue-400 hover:text-blue-500",
      bgColor: "bg-blue-400/10 hover:bg-blue-400/20",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      )
    },
  ];

  // Фильтруем навигационные ссылки, исключая текущую страницу
  const filteredNavLinks = navigationLinks.filter(link => 
    link.to !== `/${currentPage}` && !(currentPage === "index" && link.to === "/")
  );

  return (
    <>
      {/* Floating Navigation Widget - Only on desktop */}
      <div className="fixed bottom-6 right-6 z-50 space-y-3 hidden lg:flex lg:flex-col">
        {/* Quick Navigation */}
        {filteredNavLinks.length > 0 && (
          <Card className="bg-background/95 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300">
            <CardContent className="p-3">
              <div className="flex flex-col gap-2 min-w-[160px]">
                <div className="text-xs font-medium text-primary text-center mb-1">
                  Навигация
                </div>
                {filteredNavLinks.slice(0, 3).map((link) => (
                  <Button
                    key={link.to}
                    asChild
                    variant="ghost"
                    size="sm"
                    className="justify-start h-8 hover:bg-primary/10 hover:text-primary transition-all duration-200"
                  >
                    <Link to={link.to} className="flex items-center gap-2">
                      <link.icon className={`w-3 h-3 ${link.color}`} />
                      <span className="text-xs">{link.label}</span>
                    </Link>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Actions */}
        <Card className="bg-background/95 backdrop-blur-sm border shadow-lg hover:shadow-xl transition-all duration-300">
          <CardContent className="p-3">
            <div className="space-y-2 min-w-[160px]">
              <div className="text-xs font-medium text-primary text-center mb-2">
                Связаться
              </div>
              
              {/* Order Button */}
              {showOrderButton && artworkData && (
                <Button
                  onClick={() => setOrderDialogOpen(true)}
                  size="sm"
                  className="w-full text-xs gold-gradient text-black hover:scale-105 transition-transform duration-200"
                >
                  <ShoppingCart className="w-3 h-3 mr-1" />
                  Заказать работу
                </Button>
              )}

              {/* Contact Links */}
              <div className="space-y-1">
                {contactLinks.map((contact) => (
                  <a
                    key={contact.name}
                    href={contact.url}
                    target="_blank"
                    rel="noopener noreferrer"
        onClick={() => {
          if (contact.name === 'VK' && artworkData) {
            // Отправляем VK уведомление о запросе картины
            const vkMessage = `🎨 Интерес к картине!\n\n👤 Пользователь интересуется:\n📋 ${artworkData.title}\n💰 ${artworkData.price.toLocaleString()} руб.\n\n🔗 ${window.location.href}`;
            trackSocialClick(contact.name, 'боковая панель');
            
            // Логируем для диагностики (временно для тестового хостинга)
            console.log('🔵 VK уведомление о заинтересованности в картине:', {
              artwork: artworkData.title,
              price: artworkData.price,
              url: window.location.href,
              message: vkMessage
            });
          } else {
            trackSocialClick(contact.name, 'боковая панель');
          }
        }}
                    className={`flex items-center justify-center gap-2 p-2 rounded-lg transition-all duration-200 ${contact.color} ${contact.bgColor} group hover:scale-105`}
                  >
                    <div className="transition-transform group-hover:scale-110">
                      {contact.icon}
                    </div>
                    <span className="text-xs font-medium">
                      {contact.name}
                    </span>
                    <ExternalLink className="w-2 h-2 opacity-60" />
                  </a>
                ))}
                
                {/* Phone */}
                <div className="flex items-center justify-center gap-2 p-2 rounded-lg bg-primary/10 text-primary text-xs">
                  <Phone className="w-3 h-3" />
                  <span className="font-medium">+7 (908) 800-57-99</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Order Dialog */}
      {showOrderButton && artworkData && (
        <OrderDialog
          artworkTitle={artworkData.title}
          artworkId={artworkData.id}
          artworkPrice={artworkData.price}
          open={orderDialogOpen}
          onOpenChange={setOrderDialogOpen}
        />
      )}
    </>
  );
};

export default FloatingActions;